import React, { useContext, useEffect, useRef, useState } from "react";
import {
  Box, Checkbox, CircularProgress, ListItemText,
  MenuItem, Select, TextField, Typography,
} from "@mui/material";
import { makeStyles } from "tss-react/mui";
import { BRAND } from "./lightTheme";
import { AttributeType } from "services/common/Constants";
import { hasAttributeListItems } from "services/common/AttributeUtils";
import { getAccountAttributeById } from "services/edge/ContexteService";
import { getAccountAttributesByBondType, getThirdpartyAttributes, getDossierAttributes, addThirdpartyAttribute, addDossierAttribute } from "services/edge/AttributesService";
import { updateContextInstanceAttributeValue, updateContextInstanceThirdPartyAttributeValue } from "services/edge/ContexteService";
import { getDefaultShownValuesIfExist, getDefaultValuesIfExist } from "components/thirdparties/unmanagedThirdParty/UnmanagedThirdPartyUtils";
import { Context } from "states/Store";

const useStyles = makeStyles()(() => ({
  grid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "0.75rem 1.25rem",
    "@media (max-width: 560px)": { gridTemplateColumns: "1fr" },
  },
  row: {
    display: "flex",
    flexDirection: "column",
    gap: "0.3rem",
    minWidth: 0, // prevent grid cell overflow
  },
  label: {
    fontSize: "0.68rem",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.07em",
    color: BRAND.textMuted,
    fontFamily: "Inter, sans-serif",
    display: "flex",
    alignItems: "center",
    gap: "0.35rem",
  },
  required: { color: BRAND.primary },
  savedBadge: {
    fontSize: "0.62rem",
    color: "#16a34a",
    fontFamily: "Inter, sans-serif",
    fontWeight: 600,
    opacity: 1,
    transition: "opacity 0.4s",
  },
  savingDot: {
    width: "6px",
    height: "6px",
    borderRadius: "50%",
    backgroundColor: BRAND.textMuted,
    display: "inline-block",
  },
  input: {
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
      fontFamily: "Inter, sans-serif",
      fontSize: "0.825rem",
      backgroundColor: BRAND.background,
      "& fieldset": { borderColor: BRAND.border },
      "&:hover fieldset": { borderColor: BRAND.primary },
      "&.Mui-focused fieldset": { borderColor: BRAND.primary },
    },
  },
  select: {
    borderRadius: "8px !important",
    fontFamily: "Inter, sans-serif",
    fontSize: "0.825rem",
    backgroundColor: BRAND.background,
    "& fieldset": { borderColor: BRAND.border },
    "&:hover fieldset": { borderColor: BRAND.primary },
    "&.Mui-focused fieldset": { borderColor: BRAND.primary },
    "& .MuiSelect-select": {
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
  },
  placeholder: {
    color: BRAND.textMuted,
    fontStyle: "italic",
    fontSize: "0.825rem",
    fontFamily: "Inter, sans-serif",
  },
  subLabel: {
    fontSize: "0.68rem",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    color: BRAND.textMuted,
    fontFamily: "Inter, sans-serif",
    marginBottom: "0.6rem",
    display: "flex",
    alignItems: "center",
    gap: "0.4rem",
  },
  emptyText: {
    fontSize: "0.8rem",
    color: BRAND.textMuted,
    fontFamily: "Inter, sans-serif",
    fontStyle: "italic",
  },
}));

// ─── AttrRow ──────────────────────────────────────────────────────────────────
function AttrRow({ attr, value, onChange, saveState }) {
  const { classes: s } = useStyles();
  const [state] = useContext(Context);
  const [listItems, setListItems] = useState(null);
  const [loadingList, setLoadingList] = useState(false);

  useEffect(() => {
    if (!hasAttributeListItems(attr.type)) return;
    setLoadingList(true);
    (async () => {
      try {
        const res = await getAccountAttributeById(state.account.id, attr.attributeId ?? attr.id);
        setListItems(
          res.content?.values
            ?.map((x) => x.value)
            ?.sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase())) ?? []
        );
      } catch (_) { setListItems([]); }
      finally { setLoadingList(false); }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attr.attributeId, attr.id]);

  const labelText = attr.label ?? attr.id;

  const renderInput = () => {
    if (loadingList) {
      return <Box style={{ height: "36px", display: "flex", alignItems: "center", paddingLeft: "4px" }}><CircularProgress size={14} style={{ color: BRAND.primary }} /></Box>;
    }

    switch (attr.type) {
      case AttributeType.LIST_UNIQUE:
        return (
          <Select value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? [] : [e.target.value])}
            size="small" displayEmpty fullWidth className={s.select}
            MenuProps={{ disablePortal: true, PaperProps: { sx: { maxHeight: 260 } } }}
            renderValue={(v) => v ? <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block" }}>{v}</span> : <span className={s.placeholder}>—</span>}
          >
            <MenuItem value="" style={{ fontFamily: "Inter", fontSize: "0.825rem", color: BRAND.textMuted, fontStyle: "italic" }}>—</MenuItem>
            {(listItems ?? []).map((item) => (
              <MenuItem key={item} value={item} style={{ fontFamily: "Inter", fontSize: "0.825rem" }}>{item}</MenuItem>
            ))}
          </Select>
        );

      case AttributeType.LIST_MULTIPLE: {
        const selected = Array.isArray(value) ? value : [];
        return (
          <Select multiple value={selected} onChange={(e) => onChange(e.target.value)}
            size="small" displayEmpty fullWidth className={s.select}
            MenuProps={{ disablePortal: true, PaperProps: { sx: { maxHeight: 260 } } }}
            renderValue={(v) => {
              if (v.length === 0) return <span className={s.placeholder}>—</span>;
              const preview = v.slice(0, 2).join(", ");
              const suffix = v.length > 2 ? ` +${v.length - 2}` : "";
              return <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block" }}>{preview}{suffix}</span>;
            }}
          >
            {(listItems ?? []).map((item) => (
              <MenuItem key={item} value={item} style={{ fontFamily: "Inter", fontSize: "0.825rem" }}>
                <Checkbox checked={selected.includes(item)} size="small" style={{ color: BRAND.primary }} />
                <ListItemText primary={item} primaryTypographyProps={{ style: { fontFamily: "Inter", fontSize: "0.825rem" } }} />
              </MenuItem>
            ))}
          </Select>
        );
      }

      case AttributeType.BOOLEAN:
        return (
          <Select value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? [] : [e.target.value])}
            size="small" displayEmpty fullWidth className={s.select}
            MenuProps={{ disablePortal: true }}
            renderValue={(v) => v ? <span>{v === "true" ? "Oui" : "Non"}</span> : <span className={s.placeholder}>—</span>}
          >
            <MenuItem value="" style={{ fontFamily: "Inter", fontSize: "0.825rem", color: BRAND.textMuted, fontStyle: "italic" }}>—</MenuItem>
            <MenuItem value="true" style={{ fontFamily: "Inter", fontSize: "0.825rem" }}>Oui</MenuItem>
            <MenuItem value="false" style={{ fontFamily: "Inter", fontSize: "0.825rem" }}>Non</MenuItem>
          </Select>
        );

      case AttributeType.DATE:
        return (
          <TextField type="date" value={value ?? ""} onChange={(e) => onChange(e.target.value ? [e.target.value] : [])}
            size="small" fullWidth className={s.input} InputLabelProps={{ shrink: true }} />
        );

      default:
        return (
          <TextField value={value ?? ""} onChange={(e) => onChange(e.target.value ? [e.target.value] : [])}
            placeholder="—" size="small" fullWidth className={s.input}
            inputProps={{ style: { fontFamily: "Inter", fontSize: "0.825rem" } }} />
        );
    }
  };

  return (
    <Box className={s.row}>
      <Typography className={s.label}>
        {labelText}
        {attr.isRequired && <span className={s.required}>*</span>}
        {saveState === "saving" && <Box className={s.savingDot} />}
        {saveState === "saved"  && <span className={s.savedBadge}>✓</span>}
      </Typography>
      {renderInput()}
    </Box>
  );
}

// ─── LightEditableAttributes ─────────────────────────────────────────────────
/**
 * Fetches, displays and auto-saves attributes for a thirdparty, dossier or context instance.
 * Props: accountId, entityId, bondType ("thirdparty" | "dossier" | "context"), subLabel, initialValues
 * For bondType="context", entityId is the contextInstanceId and initialValues is ctx.contextAttributes
 * For bondType="context_third_party", entityId is the contextInstanceId, contextThirdpartyId is tp.id
 */
export default function LightEditableAttributes({ accountId, entityId, bondType, subLabel, onSaved, initialValues, contextThirdpartyId }) {
  const { classes: s } = useStyles();
  const [, dispatch] = useContext(Context);
  const [attrs, setAttrs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saveState, setSaveState] = useState({}); // { [label]: "saving"|"saved"|null }
  const timers = useRef({});

  useEffect(() => {
    if (!accountId || !entityId) return;
    setLoading(true);
    (async () => {
      try {
        let merged;
        if (bondType === "context") {
          // Use initialValues directly — same structure as what advanced mode uses in AttributeValueForm
          const vals = initialValues ?? [];
          merged = vals
            .map((attr) => {
              const rawValue = attr.value ?? attr.values?.[0];
              const rawValues = Array.isArray(attr.values) && attr.values.length > 0
                ? attr.values
                : (rawValue != null ? [String(rawValue)] : []);
              return { ...attr, values: rawValues };
            })
            .sort((a, b) => (a.label ?? "").localeCompare(b.label ?? ""));
        } else if (bondType === "context_third_party") {
          // Merge definitions with existing values so empty attrs are also shown
          const [defRes] = await Promise.all([
            getAccountAttributesByBondType(accountId, bondType, null),
          ]);
          const defs = defRes?.content ?? [];
          const vals = initialValues ?? [];
          const valsByLabel = Object.fromEntries(vals.map((v) => [v.label, v]));
          merged = defs
            .map((def) => {
              const existing = valsByLabel[def.label];
              const rawValues = existing
                ? (Array.isArray(existing.values) && existing.values.length > 0
                    ? existing.values
                    : (existing.value != null ? [String(existing.value)] : getDefaultValuesIfExist(def)))
                : getDefaultValuesIfExist(def);
              return {
                ...def,
                id: existing?.id ?? def.id,
                attributeId: def.id,
                accountId,
                values: rawValues,
                shownValues: existing?.shownValues ?? getDefaultShownValuesIfExist(def),
              };
            })
            .sort((a, b) => a.label.localeCompare(b.label));
        } else {
          const [defRes, valRes] = await Promise.all([
            getAccountAttributesByBondType(accountId, bondType, null),
            bondType === "thirdparty"
              ? getThirdpartyAttributes(accountId, entityId, null)
              : getDossierAttributes(accountId, entityId, null),
          ]);
          const defs = defRes?.content ?? [];
          const vals = valRes?.content ?? [];
          merged = defs
            .map((def) => {
              const existing = vals.find((v) => v.label === def.label);
              return {
                ...def,
                attributeId: def.id,
                values: existing?.values?.length ? existing.values : getDefaultValuesIfExist(def),
                shownValues: existing?.shownValues ?? getDefaultShownValuesIfExist(def),
              };
            })
            .sort((a, b) => a.label.localeCompare(b.label));
        }

        setAttrs(merged);
      } catch (_) {
        setAttrs([]);
      } finally {
        setLoading(false);
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId, entityId, bondType]);

  const handleChange = (index, newValues) => {
    const attr = attrs[index];
    const label = attr.label;

    setAttrs((prev) => prev.map((a, i) => i === index ? { ...a, values: newValues } : a));

    if (timers.current[label]) clearTimeout(timers.current[label]);
    setSaveState((prev) => ({ ...prev, [label]: "saving" }));

    timers.current[label] = setTimeout(async () => {
      try {
        const body = { label, values: newValues };
        if (bondType === "thirdparty") {
          await addThirdpartyAttribute(accountId, entityId, body, dispatch);
        } else if (bondType === "context") {
          await updateContextInstanceAttributeValue(accountId, entityId, { ...attr, label, values: newValues }, null);
        } else if (bondType === "context_third_party") {
          await updateContextInstanceThirdPartyAttributeValue(accountId, entityId, contextThirdpartyId, { ...attr, id: null, label, values: newValues }, null);
        } else {
          await addDossierAttribute(accountId, entityId, body, dispatch);
        }
        setSaveState((prev) => ({ ...prev, [label]: "saved" }));
        setTimeout(() => setSaveState((prev) => ({ ...prev, [label]: null })), 2000);
        onSaved?.();
      } catch (_) {
        setSaveState((prev) => ({ ...prev, [label]: null }));
      }
    }, 600);
  };

  if (loading) {
    return (
      <Box style={{ display: "flex", alignItems: "center", gap: "0.5rem", padding: "0.25rem 0" }}>
        <CircularProgress size={14} style={{ color: BRAND.primary }} />
        <Typography style={{ fontSize: "0.75rem", color: BRAND.textMuted, fontFamily: "Inter" }}>Chargement…</Typography>
      </Box>
    );
  }

  if (attrs.length === 0) return null;

  return (
    <Box>
      {subLabel && (
        <Typography className={s.subLabel}>
          {subLabel}
        </Typography>
      )}
      <Box className={s.grid}>
        {attrs.map((attr, i) => (
          <AttrRow
            key={attr.attributeId ?? attr.label ?? i}
            attr={attr}
            value={
              attr.type === AttributeType.LIST_MULTIPLE
                ? (attr.values ?? [])
                : (attr.values?.[0] ?? "")
            }
            onChange={(vals) => handleChange(i, vals)}
            saveState={saveState[attr.label]}
          />
        ))}
      </Box>
    </Box>
  );
}
